import React, {useEffect, useState} from "react";
import {TrashIcon} from "@heroicons/react/24/outline";
import {
  calculateAverageEmbedding,
  getStoredEmbeddingMeta,
  getStoredEmbeddings,
  STORED_ASSETS_KEY,
  USER_EMBEDDING_META_KEY,
} from "../utils";
import {
  getNeuronDebugState,
  NEURON_DEBUG_EVENT_NAME,
} from "../utils/neuronDebugStore";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const formatDateTime = (value) => {
  if (!value) return "Unavailable";

  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch (error) {
    return "Unavailable";
  }
};

const formatNumber = (value, maximumFractionDigits = 0) => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return "Unavailable";
  }

  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits,
  }).format(value);
};

const readViewedCount = () => {
  try {
    const stored = JSON.parse(localStorage.getItem("viewedUUIDs")) || [];
    return Array.isArray(stored) ? stored.length : 0;
  } catch (error) {
    return 0;
  }
};

const getEmbeddingStats = () => {
  const storedEmbeddings = getStoredEmbeddings();
  const averageEmbedding = calculateAverageEmbedding(storedEmbeddings);
  const meta = getStoredEmbeddingMeta();

  return {
    count: storedEmbeddings.length,
    dimension:
      meta?.dimension ||
      averageEmbedding.length ||
      storedEmbeddings[0]?.length ||
      null,
    updatedAt: meta?.updatedAt || null,
    preview: averageEmbedding
      .slice(0, 8)
      .map((value) => Number(value).toFixed(4))
      .join(", "),
  };
};

const InfoRow = ({label, value, monospace = false}) => (
  <div className="sm:flex sm:justify-between sm:gap-6">
    <dt className="text-sm font-medium text-gray-900 dark:text-gray-100">
      {label}
    </dt>
    <dd
      className={classNames(
        "mt-1 text-sm text-gray-900 dark:text-gray-100 sm:mt-0 sm:text-right",
        monospace && "font-mono text-xs sm:text-sm"
      )}
    >
      {value}
    </dd>
  </div>
);

const ProfilePage = ({userEmail, personalisedContent}) => {
  const [embeddingStats, setEmbeddingStats] = useState(() => getEmbeddingStats());
  const [viewedCount, setViewedCount] = useState(() => readViewedCount());
  const [debugState, setDebugState] = useState(() => getNeuronDebugState());
  const [showViewInterceptor, setShowViewInterceptor] = useState(() => {
    const stored = localStorage.getItem("neuron:viewInterceptorDebug");
    return stored === "true";
  });

  useEffect(() => {
    const syncLocalProfileData = () => {
      setEmbeddingStats(getEmbeddingStats());
      setViewedCount(readViewedCount());
    };

    const handleDebugUpdate = (event) => {
      if (event?.detail) {
        setDebugState(event.detail);
        return;
      }

      setDebugState(getNeuronDebugState());
    };

    window.addEventListener("embeddingUpdated", syncLocalProfileData);
    window.addEventListener(NEURON_DEBUG_EVENT_NAME, handleDebugUpdate);
    const handleStorage = () => {
      syncLocalProfileData();
      setDebugState(getNeuronDebugState());
    };

    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("embeddingUpdated", syncLocalProfileData);
      window.removeEventListener(NEURON_DEBUG_EVENT_NAME, handleDebugUpdate);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const handleDeleteData = () => {
    localStorage.removeItem(STORED_ASSETS_KEY);
    localStorage.removeItem(USER_EMBEDDING_META_KEY);
    localStorage.removeItem("viewedUUIDs");
    localStorage.removeItem("neuron:debugState");
    alert("Your stored data has been deleted.");
    window.location.reload();
  };

  const handleViewInterceptorToggle = (event) => {
    const enabled = event.target.checked;
    setShowViewInterceptor(enabled);
    localStorage.setItem("neuron:viewInterceptorDebug", String(enabled));
    window.dispatchEvent(
      new CustomEvent("viewInterceptorToggle", {
        detail: {enabled},
      })
    );
  };

  const latestRecommendation = debugState.latestRecommendation;
  const recommendationExperiments =
    latestRecommendation?.experiments?.filter(
      (experiment) => experiment.experimentName || experiment.variantName
    ) || [];
  const recommendationSegments = latestRecommendation?.userSegments || [];

  return (
    <div className="max-w-4xl mx-auto min-h-screen px-4 sm:px-6 lg:px-8 py-12">
      <div className="space-y-8">
        <div>
          <h3 className="text-lg font-semibold leading-6 text-gray-900 dark:text-gray-100">
            Profile Information
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-300">
            This view summarises the user and recommendation data currently
            available in the browser.
          </p>

          <dl className="mt-6 space-y-6">
            <InfoRow label="Email address" value={userEmail || "Not provided"} />
            <InfoRow label="Viewed articles" value={formatNumber(viewedCount)} />
            <InfoRow
              label="Stored interaction embeddings"
              value={formatNumber(embeddingStats.count)}
            />
            <InfoRow
              label="Embedding dimension"
              value={
                embeddingStats.dimension
                  ? formatNumber(embeddingStats.dimension)
                  : "Unavailable"
              }
            />
            <InfoRow
              label="Embedding last updated"
              value={formatDateTime(embeddingStats.updatedAt)}
            />
            <InfoRow
              label="Latest request ID"
              value={debugState.requestId || "Unavailable"}
              monospace
            />
            <InfoRow
              label="Neuron session ID"
              value={debugState.sessionId || "Unavailable"}
              monospace
            />
          </dl>
        </div>

        <div>
          <h3 className="text-lg font-semibold leading-6 text-gray-900 dark:text-gray-100">
            User Embedding
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-300">
            This is the current average embedding derived from the stories the
            user has interacted with locally on this device.
          </p>

          <div className="mt-4 rounded-2xl border border-gray-200 bg-white/80 p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900/40">
            {embeddingStats.count > 0 ? (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-3 text-sm text-gray-600 dark:text-gray-300">
                  <span>
                    Count:{" "}
                    <strong className="text-gray-900 dark:text-gray-100">
                      {formatNumber(embeddingStats.count)}
                    </strong>
                  </span>
                  <span>
                    Dimension:{" "}
                    <strong className="text-gray-900 dark:text-gray-100">
                      {embeddingStats.dimension
                        ? formatNumber(embeddingStats.dimension)
                        : "Unavailable"}
                    </strong>
                  </span>
                </div>
                <div>
                  <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Average embedding preview
                  </div>
                  <pre className="overflow-x-auto rounded-xl bg-gray-950 px-4 py-3 text-xs leading-6 text-green-200">
                    [{embeddingStats.preview}
                    {embeddingStats.dimension && embeddingStats.dimension > 8
                      ? ", ..."
                      : ""}
                    ]
                  </pre>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-300">
                No user embedding has been built yet. It will appear here after
                the user interacts with article cards that store embeddings.
              </p>
            )}
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold leading-6 text-gray-900 dark:text-gray-100">
            Latest Recommendation Response
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-300">
            These values are captured from the most recent recommendations API
            response and update each time a new response comes back.
          </p>

          <div className="mt-4 rounded-2xl border border-gray-200 bg-white/80 p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900/40">
            {latestRecommendation ? (
              <div className="space-y-6">
                <dl className="space-y-4">
                  <InfoRow
                    label="Last updated"
                    value={formatDateTime(latestRecommendation.updatedAt)}
                  />
                  <InfoRow
                    label="Message"
                    value={latestRecommendation.message || "Unavailable"}
                  />
                  <InfoRow
                    label="Request ID"
                    value={latestRecommendation.requestId || "Unavailable"}
                    monospace
                  />
                  <InfoRow
                    label="Mode"
                    value={latestRecommendation.mode || "Unavailable"}
                  />
                  <InfoRow
                    label="Pipeline ID"
                    value={
                      latestRecommendation.pipelineId != null
                        ? String(latestRecommendation.pipelineId)
                        : "Unavailable"
                    }
                  />
                  <InfoRow
                    label="Returned recommendations"
                    value={formatNumber(latestRecommendation.returnedCount)}
                  />
                  <InfoRow
                    label="Requested quantity"
                    value={
                      latestRecommendation.quantity != null
                        ? formatNumber(latestRecommendation.quantity)
                        : "Unavailable"
                    }
                  />
                  <InfoRow
                    label="Processing time"
                    value={
                      latestRecommendation.processingTimeMs != null
                        ? `${formatNumber(
                            latestRecommendation.processingTimeMs
                          )} ms`
                        : "Unavailable"
                    }
                  />
                  <InfoRow
                    label="Cold start fallback"
                    value={
                      latestRecommendation.coldStartFallbackUsed
                        ? "Used"
                        : "Not used"
                    }
                  />
                  <InfoRow
                    label="Embedding source"
                    value={
                      latestRecommendation.embeddingInfo?.source || "Unavailable"
                    }
                  />
                  <InfoRow
                    label="Used default embedding"
                    value={
                      latestRecommendation.embeddingInfo
                        ? latestRecommendation.embeddingInfo.usedDefault
                          ? "Yes"
                          : "No"
                        : "Unavailable"
                    }
                  />
                  <InfoRow
                    label="Default reason"
                    value={
                      latestRecommendation.embeddingInfo?.defaultReason ||
                      "None"
                    }
                  />
                  <InfoRow
                    label="Embedding dimension"
                    value={
                      latestRecommendation.embeddingInfo?.dimension != null
                        ? formatNumber(
                            latestRecommendation.embeddingInfo.dimension
                          )
                        : "Unavailable"
                    }
                  />
                  <InfoRow
                    label="Expected dimension"
                    value={
                      latestRecommendation.embeddingInfo?.expectedDimension !=
                      null
                        ? formatNumber(
                            latestRecommendation.embeddingInfo
                              .expectedDimension
                          )
                        : "Unavailable"
                    }
                  />
                  <InfoRow
                    label="Excluded viewed items"
                    value={
                      latestRecommendation.excludedViewedItems != null
                        ? String(latestRecommendation.excludedViewedItems)
                        : "Unavailable"
                    }
                  />
                </dl>

                <div className="space-y-3">
                  <div>
                    <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      User segments
                    </div>
                    {recommendationSegments.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {recommendationSegments.map((segment) => (
                          <span
                            key={segment}
                            className="rounded-full border border-indigo-500/40 bg-indigo-500/10 px-3 py-1 text-xs font-medium text-indigo-700 dark:text-indigo-300"
                          >
                            Segment {segment}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-300">
                        No segment data returned.
                      </p>
                    )}
                  </div>

                  <div>
                    <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Experiments
                    </div>
                    {recommendationExperiments.length > 0 ? (
                      <div className="space-y-2">
                        {recommendationExperiments.map((experiment) => (
                          <div
                            key={`${
                              experiment.experimentId || experiment.experimentName
                            }-${experiment.variantId || experiment.variantName}`}
                            className="rounded-xl border border-gray-200 px-3 py-2 text-sm dark:border-gray-800"
                          >
                            <div className="font-medium text-gray-900 dark:text-gray-100">
                              {experiment.experimentName || "Experiment"}
                            </div>
                            <div className="text-gray-500 dark:text-gray-300">
                              {experiment.variantName || "Variant"}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-300">
                        No experiment metadata returned.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-300">
                No recommendation response has been captured yet in this
                browser session.
              </p>
            )}
          </div>
        </div>

        <div className="pt-8">
          <h3 className="text-lg font-semibold leading-6 text-gray-900 dark:text-gray-100">
            Manage Data
          </h3>
          <div className="mt-4">
            <button
              onClick={handleDeleteData}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none"
            >
              <TrashIcon className="h-5 w-5 mr-2" aria-hidden="true" />
              Delete Data
            </button>
          </div>
        </div>

        <div className="py-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <label className="flex items-center gap-3 text-sm text-gray-900 dark:text-gray-100">
              <h2 className="font-semibold text-lg">Debug tools</h2>
              <span className="relative inline-flex h-6 w-11 flex-shrink-0">
                <input
                  type="checkbox"
                  className="peer sr-only"
                  checked={showViewInterceptor}
                  onChange={handleViewInterceptorToggle}
                />
                <span className="absolute inset-0 rounded-full bg-gray-200 transition peer-checked:bg-indigo-600 dark:bg-gray-800" />
                <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition peer-checked:translate-x-5" />
              </span>
            </label>
          </div>
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-300">
            When enabled, the view interceptor overlay and related debug
            notifications are shown across the app.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
